// lib/list/factoryObjects.ts
import { z } from 'zod';
import type { FactoryManagementScope, FactoryObject, FactoryObjectDimensions, FactoryObjectDto } from '#types/types';
import { factoryPersonaSchema } from './factoryPersonas';

const revision = z.number().int().positive().safe();
const quantity = z.number().finite().positive().max(1_000_000).optional();
export const factoryObjectDimensionsSchema = z.object({
  width: quantity, height: quantity, depth: quantity,
  unit: z.enum(['mm', 'cm', 'm', 'in']), provenance: z.enum(['user-supplied', 'estimated']),
  note: z.string().trim().max(500),
}).strict();
const base = z.object(factoryPersonaSchema.shape);
const sheet = base.shape.sheet.unwrap().omit({ identityRevision: true, model: true }).extend({ contentRevision: revision, revision: revision.optional(), model: z.literal('nano-banana-2') });
export const factoryObjectSchema = base.omit({ identityRevision: true, sheet: true, operation: true }).extend({
  contentRevision: revision, dimensions: factoryObjectDimensionsSchema,
  sheet: sheet.optional(), sheetHistory: z.array(sheet.extend({ revision })).max(1000).optional(),
  operation: base.shape.operation.unwrap().omit({ identityRevision: true, model: true, imageSize: true }).extend({ contentRevision: revision, model: z.literal('nano-banana-2'), imageSize: z.literal('4K') }).optional(),
}).strict().superRefine((object, context) => {
  const references = object.referenceImages;
  if (new Set(references.map(image => image.id)).size !== references.length
    || references.reduce((sum, image) => sum + image.imageAsset.bytes, 0) > 80 * 1024 * 1024
    || [...references, ...(object.sheet ? [object.sheet] : []), ...(object.sheetHistory ?? [])].some(image => [image.imageAsset, image.thumbnailAsset].some(asset => !asset.path.startsWith(`factory-personas/${object.id}/`)))
    || new Set(object.sheetHistory?.map(panel => panel.revision)).size !== (object.sheetHistory?.length ?? 0)
    || (object.sheet && object.sheet.contentRevision !== object.contentRevision)
    || (object.status === 'ready' && !object.sheet)
    || (object.status === 'generating' && (!object.operation || object.operation.contentRevision !== object.contentRevision))) {
    context.addIssue({ code: z.ZodIssueCode.custom, message: 'Invalid object reference or panel state.' });
  }
});
export const readFactoryObjects = (value: unknown): FactoryObject[] | null => {
  const parsed = z.array(factoryObjectSchema).safeParse(value ?? []);
  if (!parsed.success || parsed.data.filter(object => object.deletedAt === null).length > 100
    || new Set(parsed.data.map(object => object.id)).size !== parsed.data.length
    || new Set(parsed.data.map(object => object.handle)).size !== parsed.data.length) return null;
  return parsed.data;
};
export const objectPanelExpired = (object: FactoryObject): boolean => object.status === 'generating'
  && Boolean(object.operation && Date.now() - object.operation.startedAt > 10 * 60 * 1000);

export const rememberFactoryObjectSheet = (object: FactoryObject): FactoryObject => {
  if (!object.sheet) return object;
  const panel = { ...object.sheet, revision: object.sheet.revision ?? object.revision };
  return { ...object, sheet: panel, sheetHistory: [...(object.sheetHistory ?? []).filter(item => item.revision !== panel.revision), panel] };
};

export const factoryObjectDto = (object: FactoryObject, scope: FactoryManagementScope): FactoryObjectDto => {
  const url = (referenceId: string, variant: string) => '/api/list/factory-objects/assets?' + new URLSearchParams({
    objectId: object.id, referenceId, variant,
    ...(referenceId === 'sheet' ? { version: String(object.sheet?.revision ?? object.revision) } : {}),
    ...(scope.organisationId !== undefined ? { organisationId: scope.organisationId } : { workspaceId: scope.workspaceId }),
  });
  return {
    id: object.id, handle: object.handle, name: object.name, notes: object.notes, dimensions: object.dimensions,
    revision: object.revision, contentRevision: object.contentRevision,
    status: objectPanelExpired(object) ? 'failed' : object.status,
    ...(objectPanelExpired(object) ? { error: 'The panel attempt expired. Start a new explicit attempt.' } : object.error ? { error: object.error } : {}),
    ...(object.operation ? { operationId: object.operation.id } : {}),
    referenceImages: object.referenceImages.map(image => ({ id: image.id, url: url(image.id, 'original'), thumbnailUrl: url(image.id, 'thumbnail') })),
    ...(object.sheet ? { sheet: { url: url('sheet', 'original'), thumbnailUrl: url('sheet', 'thumbnail'), contentRevision: object.sheet.contentRevision, model: object.sheet.model } } : {}),
  };
};
export const objectDimensionSummary = (dimensions: FactoryObjectDimensions): string => {
  const values = (['width', 'height', 'depth'] as const).map(key => `${key}: ${dimensions[key] === undefined ? 'Not supplied' : `${dimensions[key]} ${dimensions.unit}`}`).join(' · ');
  return `${values}${[dimensions.width, dimensions.height, dimensions.depth].some(value => value !== undefined) ? ` · ${dimensions.provenance === 'estimated' ? 'Estimated, not verified' : 'User supplied'}` : ''}${dimensions.note ? ` · ${dimensions.note}` : ''}`;
};
export const convertObjectDimensionUnit = (dimensions: FactoryObjectDimensions, unit: FactoryObjectDimensions['unit']): FactoryObjectDimensions => {
  const millimeters = { mm: 1, cm: 10, m: 1000, in: 25.4 };
  return { ...dimensions, unit, ...Object.fromEntries((['width', 'height', 'depth'] as const).flatMap(key => dimensions[key] === undefined ? [] : [[key, Number((dimensions[key]! * millimeters[dimensions.unit] / millimeters[unit]).toPrecision(12))]])) };
};
